import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { Card, Stack, Group, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Switch, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatDate, formatMinor } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';

/**
 * One business's extra use (owner, 30 October 2026): whether it is on, how it is paid, its limit and this month's
 * figures, why it is paused, and a switch that blocks it for this business. A block pauses it at once, so spending
 * stops at zero again; what is already owed is still charged.
 */
export function ExtraUseCard({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const t = useT();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const key = ['admin', 'org-extra-use', tenantId];
  const q = useQuery({ queryKey: key, queryFn: () => admin.getOrgExtraUse(tenantId), retry: false });
  const v = q.data?.data;

  const block = useMutation({
    mutationFn: (blocked: boolean) => admin.setOrgExtraUseBlocked(tenantId, blocked, blocked ? reason : null),
    onSuccess: (res) => {
      qc.setQueryData(key, res);
      qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      setReason('');
      notifySuccess(
        res.data.platform_blocked ? t('tenants.view.extraUse.blocked', { name: tenantName }) : t('tenants.view.extraUse.unblocked', { name: tenantName }),
        t('tenants.view.extraUse.title')
      );
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('tenants.view.extraUse.title'))
  });

  const money = (minor: number | null | undefined) => formatMinor(minor ?? 0, v?.currency ?? 'ZAR');
  const how = !v?.mode ? null
    : v.mode === 'steps' ? t('tenants.view.extraUse.mode.steps', { amount: money(v.step_minor) })
    : v.mode === 'upfront' ? t('tenants.view.extraUse.mode.upfront', { amount: money(v.topup_minor), below: money(v.topup_below_minor ?? 5000) })
    : t('tenants.view.extraUse.mode.month_end');
  const badge = !v ? null
    : v.platform_blocked ? <Badge size="sm" variant="light" color="red">{t('tenants.view.extraUse.badge.blocked')}</Badge>
    : v.active ? <Badge size="sm" variant="light" color="green">{t('tenants.view.extraUse.badge.active')}</Badge>
    : v.enabled ? <Badge size="sm" variant="light" color="orange">{t('tenants.view.extraUse.badge.paused')}</Badge>
    : <Badge size="sm" variant="light" color="gray">{t('tenants.view.extraUse.badge.off')}</Badge>;

  return (
    <Card withBorder data-testid="org-extra-use">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Title order={4}>{t('tenants.view.extraUse.title')}</Title>
          {badge}
        </Group>
        {q.error && <Alert color="warning">{t('tenants.view.extraUse.loadError')}</Alert>}
        {v && (
          <Stack gap={4}>
            <Text role="bodySmall">
              {v.on_plan
                ? t('tenants.view.extraUse.onPlan', { plan: v.plan_name ?? v.plan_code ?? '' })
                : t('tenants.view.extraUse.notOnPlan', { plan: v.plan_name ?? v.plan_code ?? '' })}
              {' · '}
              {v.has_card ? t('tenants.view.extraUse.card', { card: v.card_label ?? '' }) : t('tenants.view.extraUse.noCard')}
            </Text>
            <Text role="bodySmall" data-testid="org-extra-use-settings">
              {v.enabled ? t('tenants.view.extraUse.settings', { how: how ?? '', limit: money(v.limit_minor) }) : t('tenants.view.extraUse.off')}
            </Text>
            {(v.enabled || v.used_minor > 0) && (
              <Text role="bodySmall" data-testid="org-extra-use-month">
                {t('tenants.view.extraUse.month', { month: v.month, used: money(v.used_minor), limit: money(v.limit_minor), owed: money(v.owed_minor), charged: money(v.charged_minor) })}
              </Text>
            )}
            {v.enabled && v.paused_reason && (
              <Text role="bodySmall" data-testid="org-extra-use-paused">
                {v.paused_at
                  ? t('tenants.view.extraUse.paused', { reason: t(`tenants.view.extraUse.reason.${v.paused_reason}`, v.paused_reason), date: formatDate(v.paused_at) })
                  : t('tenants.view.extraUse.pausedNow', { reason: t(`tenants.view.extraUse.reason.${v.paused_reason}`, v.paused_reason) })}
                {v.decline_reason ? ` ${t('tenants.view.extraUse.declineReason', { reason: v.decline_reason })}` : ''}
              </Text>
            )}
            {v.platform_blocked && v.blocked_reason && (
              <Text role="caption" c="var(--rr-color-text-muted)">{t('tenants.view.extraUse.blockedReason', { reason: v.blocked_reason })}</Text>
            )}
          </Stack>
        )}
        {v && (
          <Stack gap="xs">
            {!v.platform_blocked && (
              <TextInput
                size="sm"
                label={t('tenants.view.extraUse.reasonLabel')}
                description={t('tenants.view.extraUse.reasonHint')}
                value={reason}
                onChange={(e) => setReason(e.currentTarget.value)}
                maxLength={500}
              />
            )}
            <Switch
              checked={v.platform_blocked}
              onChange={(on) => block.mutate(on)}
              disabled={block.isPending}
              label={t('tenants.view.extraUse.block')}
              description={t('tenants.view.extraUse.blockHint')}
              data-testid="org-extra-use-block"
            />
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
